import { useLazyQuery, useMutation } from '@apollo/client';
import { FORGOT_PASSWORD, RESET_LOGIN_TOKEN, RESET_PASSWORD, VERIFY_CONFIRMATION_CODE, } from '@src/graphql/queries/user';
/**
 * Custom hook that provides functions to handle forgot password, reset password, and verify confirmation code operations.
 *
 * @returns {Object} An object containing the following functions:
 * - forgotPassword: Initiates the forgot password process by sending an email.
 * - resetLoginToken: Resets the login token for a user based on their email and client ID.
 * - resetPassword: Resets the user's password using the provided token, new password, email, and confirmation code.
 * - verifyConfirmationCode: Verifies the confirmation code for the given email.
 */
export const useForgotPassword = () => {
    const [forgotPasswordMutation] = useMutation(FORGOT_PASSWORD);
    const [resetLoginTokenMutation] = useMutation(RESET_LOGIN_TOKEN);
    const [resetPasswordMutation] = useMutation(RESET_PASSWORD);
    const [verifyConfirmationCodeQuery] = useLazyQuery(VERIFY_CONFIRMATION_CODE);
    /**
     * Sends a forgot password request for the given email.
     *
     * @param {string} email - The email address to send the forgot password request to.
     * @returns {Promise<any>} The response from the forgot password mutation.
     */
    const forgotPassword = async (email) => {
        const response = await forgotPasswordMutation({
            variables: {
                email,
            },
        });
        return response;
    };
    /**
     * Resets the login token for a user based on their employee record ID (not uniqueId) and client ID.
     *
     * @param {number} employeeId - The employee record ID (not uniqueId) associated with the user.
     * @returns {Promise<any>} The response from the reset login token mutation.
     */
    const resetLoginToken = async (employeeId) => {
        const response = await resetLoginTokenMutation({
            variables: {
                employeeId,
            },
        });
        return response;
    };
    /**
     * Resets the user's password using the provided token, new password, email, and confirmation code.
     *
     * @param {string} token - The token used to authorize the password reset.
     * @param {string} password - The new password to set for the user.
     * @param {string} email - The email address associated with the user's account.
     * @param {string} confirmationCode - The confirmation code sent to the user's email.
     * @param {boolean} [autoLogin=false] - Whether to automatically log in the user after resetting their password.
     * @returns {Promise<any>} The response from the password reset mutation.
     */
    const resetPassword = async (token, password, email, confirmationCode, autoLogin) => {
        const response = await resetPasswordMutation({
            variables: {
                input: {
                    token,
                    password,
                    email,
                    confirmationCode,
                    autoLogin,
                },
            },
        });
        return response;
    };
    /**
     * Verifies the confirmation code for the given email.
     *
     * @param {string} email - The email address to verify the confirmation code for.
     * @param {string} confirmationCode - The confirmation code to verify.
     * @returns {Promise<any>} A promise that resolves to the response of the verification query.
     */
    const verifyConfirmationCode = async (email, confirmationCode) => {
        const response = await verifyConfirmationCodeQuery({
            variables: {
                email,
                confirmationCode,
            },
        });
        return response;
    };
    return {
        forgotPassword,
        resetLoginToken,
        resetPassword,
        verifyConfirmationCode,
    };
};
